import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { isProduction } from "@/lib/comparatifs";

export default function ComparatifsTopAlternatives() {
  const canonicalUrl = "https://www.cosmos-wave.com/comparatifs/top-alternatives-agences-amazon";
  const title = "Meilleures alternatives agences Amazon (2025) | Cosmos Wave";
  const description = "Guide complet pour choisir votre agence Amazon : critères clés, erreurs à éviter, checklist. Comparaison objective des profils selon vos besoins.";

  const faqItems = [
    {
      question: "Quels critères prioritaires pour choisir une agence Amazon ?",
      answer: "Stack technique (IA/SP-API), spécialisations (Ads/SEO/Ops), méthode (data-driven vs intuition), zones géographiques, références sectorielles et transparence tarifaire."
    },
    {
      question: "Comment éviter les pièges lors de la sélection ?",
      answer: "Exiger des cas d'usage concrets, vérifier les certifications Amazon, demander l'accès aux outils/dashboards, clarifier les KPIs mesurés et éviter les contrats trop longs."
    },
    {
      question: "Quel budget réaliste pour une agence Amazon performante ?",
      answer: "5-15K€/mois pour une agence spécialisée, 15-50K€/mois pour du full-service, selon votre CA Amazon et la complexité (catalogue, pays, automation requise)."
    }
  ];

  const alternatives = [
    {
      type: "Agence IA & SP-API (Cosmos Wave type)",
      description: "Spécialiste automation, Buy Box, Catalog Health avec stack IA propriétaire",
      budget: "5-20K€/mois",
      ideal: "Problèmes catalogue, besoin IA, résultats rapides"
    },
    {
      type: "Agence 360° full-service",
      description: "Équipes Ads, SEO, Creative, Expansion EU avec approche traditionnelle",
      budget: "15-50K€/mois", 
      ideal: "Marques établies, multi-pays, budget conséquent"
    },
    {
      type: "Boutique spécialisée",
      description: "Focus unique (Ads ou SEO ou Ops) avec expertise poussée",
      budget: "8-25K€/mois",
      ideal: "Besoin spécifique, secteur de niche"
    },
    {
      type: "Freelance/Collectif expert",
      description: "Expert indépendant ou petit collectif avec approche personnalisée",
      budget: "2-8K€/mois",
      ideal: "Budget optimisé, relation directe, flexibilité"
    }
  ];

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Meilleures alternatives agences Amazon",
    "description": description,
    "itemListElement": alternatives.map((alt, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": alt.type,
      "description": alt.description
    }))
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage", 
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem", 
        "position": 1,
        "name": "Accueil",
        "item": "https://www.cosmos-wave.com/"
      },
      {
        "@type": "ListItem",
        "position": 2, 
        "name": "Comparatifs",
        "item": "https://www.cosmos-wave.com/comparatifs"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Top alternatives agences Amazon",
        "item": canonicalUrl
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        {!isProduction() && <meta name="robots" content="noindex,nofollow" />}
        
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Cosmos Wave" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content="https://www.cosmos-wave.com/og/comparatifs-hub-1200x630.jpg" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content="https://www.cosmos-wave.com/og/comparatifs-hub-1200x630.jpg" />
        
        <script type="application/ld+json">{JSON.stringify(itemListSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-background">
        
        
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            <header className="text-center mb-16">
              <h1 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
                Meilleures Alternatives
                <span className="block text-primary mt-2">Agences Amazon (2025)</span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Guide complet pour choisir votre agence Amazon : critères clés, erreurs à éviter, checklist téléchargeable.
                Comparaison objective des profils selon vos besoins.
              </p>
            </header>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-6 text-foreground">Critères de choix essentiels</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-card rounded-lg p-6 border border-border">
                  <h3 className="font-semibold text-foreground mb-4">Technique & Méthode</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• <span className="font-medium">Stack IA/SP-API</span> : automation, données temps réel</li>
                    <li>• <span className="font-medium">Approche data-driven</span> : KPIs mesurables, reporting transparent</li>
                    <li>• <span className="font-medium">Spécialisations</span> : Ads, SEO, Ops, Buy Box</li>
                    <li>• <span className="font-medium">Outils propriétaires</span> : dashboards, alertes, prédictions</li>
                  </ul>
                </div>
                <div className="bg-card rounded-lg p-6 border border-border">
                  <h3 className="font-semibold text-foreground mb-4">Business & Contrat</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• <span className="font-medium">Références sectorielles</span> : cas d'usage similaires</li>
                    <li>• <span className="font-medium">Zones géographiques</span> : FR, EU, UK, conformité DMA</li>
                    <li>• <span className="font-medium">Modèle tarifaire</span> : fixe, %, hybride, transparence</li>
                    <li>• <span className="font-medium">Flexibilité</span> : pas de lock-in, formation équipes</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-6 text-foreground">Erreurs fréquentes à éviter</h2>
              <div className="bg-destructive/5 rounded-lg p-6 border border-destructive/20">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold text-foreground mb-3">Pièges techniques</h3>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• Pas d'accès direct aux outils/données</li>
                      <li>• Méthodes opaques sans KPIs clairs</li>
                      <li>• Pas de formation/documentation</li>
                      <li>• Stack technique obsolète</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-3">Pièges contractuels</h3>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• Contrats longs (24 mois+) sans résultats</li>
                      <li>• Frais cachés ou % sur dépenses Ads</li>
                      <li>• Pas de clause de sortie</li>
                      <li>• Promesses irréalistes sans garanties</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-6 text-foreground">4 Profils d'agences recommandés</h2>
              <div className="space-y-6">
                {alternatives.map((alternative, index) => (
                  <div key={index} className="bg-card rounded-lg p-6 border border-border">
                    <div className="grid md:grid-cols-3 gap-4 items-center">
                      <div>
                        <h3 className="text-lg font-semibold text-foreground mb-2">{alternative.type}</h3>
                        <p className="text-muted-foreground text-sm">{alternative.description}</p>
                      </div>
                      <div>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary/10 text-primary">
                          {alternative.budget}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">
                          <span className="font-medium">Idéal pour :</span> {alternative.ideal}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-6 text-foreground">Checklist de sélection</h2>
              <div className="bg-primary/5 rounded-lg p-6 border border-primary/20">
                <h3 className="font-semibold text-foreground mb-4">Questions à poser aux agences</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Capacités techniques</h4>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      <li>□ Utilisez-vous l'IA et SP-API ?</li>
                      <li>□ Puis-je accéder aux dashboards ?</li>
                      <li>□ Comment mesurez-vous le Buy Box ?</li>
                      <li>□ Quelle formation fournissez-vous ?</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Références & contrat</h4>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      <li>□ Cas d'usage dans mon secteur ?</li>
                      <li>□ Durée d'engagement minimum ?</li>
                      <li>□ KPIs garantis et délais ?</li>
                      <li>□ Clause de sortie flexible ?</li>
                    </ul>
                  </div>
                </div>
                <div className="mt-6 text-center">
                  <div className="bg-card rounded-lg p-4 border border-border inline-block">
                    <p className="text-sm text-muted-foreground mb-2">
                      <span className="font-medium">Checklist complète téléchargeable</span> (PDF + Excel)
                    </p>
                    <button 
                      className="px-4 py-2 bg-primary text-primary-foreground text-sm rounded-lg hover:bg-primary/90 transition-colors"
                      onClick={() => alert("Checklist bientôt disponible - contactez-nous pour l'obtenir en avant-première")}
                    >
                      Télécharger la checklist
                    </button>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-6 text-foreground">Questions fréquentes</h2>
              <div className="space-y-4">
                {faqItems.map((item, index) => (
                  <div key={index} className="bg-card rounded-lg p-6 border border-border">
                    <h3 className="font-semibold text-foreground mb-3">{item.question}</h3>
                    <p className="text-muted-foreground">{item.answer}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-12 text-center">
              <div className="bg-primary/5 rounded-lg p-8">
                <h2 className="text-2xl font-semibold mb-4 text-foreground">
                  Audit gratuit en 48-72h
                </h2>
                <p className="text-muted-foreground mb-6">
                  Diagnostic IA 360° pour identifier le profil d'agence optimal selon vos besoins Amazon actuels.
                </p>
                <Link 
                  to="/ia/audit" 
                  className="inline-flex items-center justify-center px-6 py-3 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Demander un audit gratuit
                </Link>
              </div>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold mb-4 text-foreground">Ressources utiles</h3>
              <div className="flex flex-wrap gap-4 text-sm">
                <Link to="/comparatifs" className="text-primary hover:underline">Tous les comparatifs</Link>
                <Link to="/ia" className="text-primary hover:underline">Solutions IA Amazon</Link>
                <Link to="/services/amazon" className="text-primary hover:underline">Services Amazon</Link>
                <Link to="/services/buybox-sprint" className="text-primary hover:underline">Sprint Buy Box 15j</Link>
                <Link to="/ia/agents-assistants-amazon/impl" className="text-primary hover:underline">Agent IA 30j</Link>
                <Link to="/contact" className="text-primary hover:underline">Contact expert</Link>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}